/**
 * One field of an `ActionForm`, drawn with session 09's controls.
 *
 * A page never renders one of these directly — `ActionForm` maps over
 * `fieldsFor(action)` — but it is its own file because it is the whole of the
 * "widget → markup" table and `ActionForm` is the whole of "what a form does". The
 * four widgets that are not a single control (`money`, `tags`, `multiselect`,
 * `rows`) are the reason: each is two or more of the contract's own controls
 * composed, and none of them is a new class (`mockups/assets/CONTRACT.md` §5 — a
 * page composes primitives and never writes CSS).
 *
 * **Three of the widgets are pickers over a read, and that is session 24's change.**
 * `client`, `service` and `assignee` were a slug box, a code box and a uuid box, on
 * the argument that "every studio page that opens a form already knows which client
 * it is about and passes it through `fixed`". That was true of the client and never
 * true of the other two — R3-04 found `tasks_assign` offering a bare
 * `<input name="assignee_id">` whose help text was the tool's own parameter
 * documentation, and R3-09 found *New task* asking a studio member to type
 * `casaturca` and refusing `Casaturca`. Each picker reads the list it needs and each
 * says so out loud when the read fails, because a picker that silently renders no
 * options is a field nobody can fill in.
 *
 * The reads are the published ones and nothing here joins two of them: the client
 * directory is `clients_list` (staff-open; `clients_summary` stays the manager book),
 * the rate card is `catalog_list_services`, and the studio directory is
 * `tasks_list_assignees`, which session 24 added for exactly this.
 */

import type { TaskView } from '@fareast/shared'
import type { Control, FieldValues } from 'react-hook-form'
import { useController, useFieldArray } from 'react-hook-form'

import { useStudioRead } from '../../api/studio.js'
import { usePrincipal } from '../../auth/principal.js'
import { Button, ButtonRow } from '../portal/Button.js'
import {
  Check,
  Hint,
  Input,
  Label,
  Select,
  Textarea,
  type SelectChoice,
  type SelectOption,
} from '../portal/Form.js'
import { Row, Stack } from '../portal/Layout.js'
import type { FieldSpec } from '../portal/schemaFields.js'
import { Small } from '../portal/Text.js'
import { emptyRow, type FormValues } from './actionSchema.js'
import { AGENT_NOUN, unassignQuestion } from './taskWords.js'

export type ActionFieldProps = {
  readonly field: FieldSpec
  readonly control: Control<FormValues>
  /** Prefix for a field inside a `rows` row — `lines.0.`. Empty at the top level. */
  readonly prefix?: string
  /** Values this field's list must not offer here (`ActionForm` `without`). */
  readonly without?: readonly string[] | undefined
}

/** A list with the choices a window may not offer taken out — out of its groups too. */
function offered(
  choices: readonly SelectChoice[],
  without: readonly string[] | undefined,
): readonly SelectChoice[] {
  if (without === undefined || without.length === 0) return choices
  return choices.flatMap((choice): SelectChoice[] =>
    'options' in choice
      ? [{ ...choice, options: choice.options.filter((one) => !without.includes(one.value)) }]
      : without.includes(choice.value)
        ? []
        : [choice],
  )
}

export function ActionField({ field, control, prefix = '', without }: ActionFieldProps) {
  const name = `${prefix}${field.name}`
  const label = field.optional ? `${field.label} (optional)` : field.label
  const wired = { control, name, label }
  const hint = field.hint

  switch (field.widget) {
    case 'checkbox':
      return <Check {...wired} hint={hint} />

    case 'textarea':
      return <Textarea {...wired} hint={hint} />

    case 'select':
      return <Select {...wired} hint={hint} options={offered(field.options ?? [], without)} />

    case 'number':
    case 'qty':
      return <Input {...wired} hint={hint} type="number" min={field.min} max={field.max} />

    case 'date':
      return <Input {...wired} hint={hint ?? 'YYYY-MM-DD'} placeholder="2026-08-31" />

    case 'period':
      return <Input {...wired} hint={hint ?? 'YYYY-MM'} placeholder="2026-08" />

    case 'ref':
      return <Input {...wired} hint={hint} placeholder="FES-2026-047" />

    case 'client':
      return <ClientField {...wired} hint={hint} optional={field.optional} without={without} />

    case 'service':
      return <ServiceField {...wired} hint={hint} optional={field.optional} without={without} />

    case 'assignee':
      return <AssigneeField {...wired} hint={hint} without={without} />

    case 'datetime':
      return (
        <Input
          {...wired}
          hint={hint ?? 'The day and time, in your own zone.'}
          type="datetime-local"
        />
      )

    case 'money':
      return <MoneyField field={field} control={control} name={name} label={label} />

    case 'tags':
      return <Textarea {...wired} hint={hint ?? 'One per line.'} />

    case 'multiselect':
      return <MultiSelectField field={field} control={control} name={name} label={label} />

    case 'rows':
      return <RowsField field={field} control={control} name={name} label={label} />

    default:
      return <Input {...wired} hint={hint} />
  }
}

/* ----------------------------------------------------------- the three pickers */

type PickerProps<Values extends FieldValues = FormValues> = {
  readonly control: Control<Values>
  readonly name: string
  readonly label: string
  readonly hint?: string | undefined
  readonly optional?: boolean
  readonly without?: readonly string[] | undefined
}

/**
 * What every picker says when the list behind it could not be read.
 *
 * A `<select>` with a placeholder and no options is a control that looks fillable and
 * is not, so the reason goes where the hint would be — `Select` renders `hint` under
 * the box, and the box is disabled beside it.
 *
 * **Only where there is nothing**: a list is read again after a write to its module,
 * and a re-read that failed said "nothing to choose from" under a box that still
 * listed everybody.
 */
function pickerHint(
  hint: string | undefined,
  state: { readonly isPending: boolean; readonly isError: boolean; readonly data: unknown },
  what: string,
): string | undefined {
  if (state.isError && state.data === undefined) {
    return `We could not read ${what}, so there is nothing to choose from.`
  }
  if (state.isPending) return `Reading ${what}…`
  return hint
}

/**
 * A client, chosen from the directory — R3-09.
 *
 * Uses `clients_list` (open to the restricted studio tier) rather than
 * `clients_summary` (manager-only, MRR book). `tasks_create` is staff-open, so the
 * picker must not depend on a manager read. The value is the **slug**, because that
 * is what every tool takes ("`clientRef` is how an action names its client").
 *
 * An optional `client` really is optional — `tasks_create` without one is internal
 * studio work — so the placeholder says so rather than reading as an unanswered
 * question.
 */
function ClientField({ control, name, label, hint, optional, without }: PickerProps) {
  const clients = useStudioRead(
    'clients_list',
    { include_archived: false, limit: 100 },
    { inline: true },
  )
  const options: SelectChoice[] = [
    ...blankChoice(optional, 'Internal — no client'),
    ...(clients.data?.clients ?? []).map((row) => ({ value: row.slug, label: row.name })),
  ]

  return (
    <Select
      control={control}
      name={name}
      label={label}
      hint={pickerHint(hint, clients, 'the client directory')}
      options={offered(options, without)}
      disabled={clients.isPending}
      placeholder="Choose a client"
    />
  )
}

/**
 * "Leave it blank" as a real option, for a picker whose field is optional.
 *
 * `Select`'s placeholder is a **disabled** `<option value="">` — which is right for an
 * unset required field and wrong here: a `tasks_create` with no client is studio work
 * that belongs to nobody, and choosing a client and then changing your mind has to be
 * possible. An option with an empty value is chosen back, and `bodyOf` leaves a blank
 * field out of the body entirely, which is what "no client" means on the wire.
 *
 * Over a row that already has one — *Change service* on a task — the same option is
 * how the service is **taken off**, and there the blank is sent as the `null` the
 * schema takes for it (`schemaForm.ts` `clearedOf`). Where a schema has no way to take
 * the value away, `ActionForm` draws the field as required, and this offers nothing:
 * a choice that would be silently ignored is not one.
 */
function blankChoice(optional: boolean | undefined, label: string): SelectOption[] {
  return optional === true ? [{ value: '', label }] : []
}

/**
 * A service, chosen from the rate card — R3-09.
 *
 * Grouped the way the card itself is (`SERVICE_CATEGORIES`): a monthly block, project
 * work and metered usage are three different things to be doing, and a flat list of
 * sixteen said so nowhere. `catalog_list_services` is the one read; the categories are
 * the API's own field and nothing here decides which service is which.
 */
function ServiceField({ control, name, label, hint, optional, without }: PickerProps) {
  const services = useStudioRead('catalog_list_services', {}, { inline: true })
  const rows = services.data?.services ?? []
  const options: SelectChoice[] = [
    ...blankChoice(optional, 'Not against a service'),
    ...SERVICE_GROUPS.map((group) => ({
      label: group.label,
      options: rows
        .filter((one) => one.category === group.category)
        .map((one) => ({ value: one.code, label: one.name })),
    })).filter((group) => group.options.length > 0),
  ]

  return (
    <Select
      control={control}
      name={name}
      label={label}
      hint={pickerHint(hint, services, 'the rate card')}
      options={offered(options, without)}
      disabled={services.isPending}
      placeholder="Choose a service"
    />
  )
}

/** `SERVICE_CATEGORIES`' three, in the order the card prints them. */
const SERVICE_GROUPS = [
  { category: 'monthly', label: 'Monthly blocks' },
  { category: 'project', label: 'Project work' },
  { category: 'usage', label: 'Metered by the widget' },
] as const

/**
 * Who a task goes to — the one control that answers **two** of its action's fields.
 *
 * R3-04: this was an `Assignee kind` select beside a plain text box for a uuid, and
 * typing "Derya T." gave `Invalid UUID`. The two are not two questions — the API's own
 * refusal says "an assignee is a person or an agent with an id, or nobody at all" —
 * so one list holds all three answers and `ActionForm` `bodyOf` splits the pair apart
 * again on the way out.
 *
 * The value is `"<kind>:<id>"`, or `"none"`. A composite rather than two controls
 * because the pair is what `tasks_list_assignees` answers with, and because the
 * illegal combinations (a kind with no id, an id with kind `none`) then cannot be
 * expressed at all.
 *
 * **An agent is not a person**, so it is a group of its own with the noun `TaskCardFull`
 * uses everywhere else — a board that says "delivery-bot — an agent" and a form that
 * lists it beside four colleagues would be two answers to one question.
 */
function AssigneeField({ control, name, label, hint, without }: PickerProps) {
  const principal = usePrincipal()
  const people = useStudioRead('tasks_list_assignees', {}, { inline: true })
  const rows = people.data?.assignees ?? []
  const meId = principal.kind === 'staff_user' ? principal.userId : null
  const me = meId === null ? null : rows.find((one) => one.kind === 'user' && one.id === meId)

  const options: SelectChoice[] = [
    { value: NOBODY, label: 'Unassigned' },
    ...(me === undefined || me === null
      ? []
      : [{ value: assigneeValue(me.kind, me.id), label: 'Me' }]),
    ...groupOf(
      'The studio',
      rows.filter((one) => one.kind === 'user' && one.id !== meId),
    ),
    ...groupOf(
      `Agents — ${AGENT_NOUN} is not a colleague`,
      rows.filter((one) => one.kind === 'agent'),
    ),
  ]

  return (
    <Select
      control={control}
      name={name}
      label={label}
      hint={pickerHint(hint, people, 'the studio directory')}
      options={offered(options, without)}
      disabled={people.isPending}
      placeholder="Choose who it goes to"
    />
  )
}

/** The value that means "take it off whoever has it". `ActionForm` reads the same one. */
export const NOBODY = 'none'

/** `confirm` for a `tasks_assign` form over these tasks: ask before Unassigned (#48). */
export function confirmUnassign(tasks: readonly Pick<TaskView, 'ref' | 'status'>[]) {
  return (values: FormValues) => (values['assignee'] === NOBODY ? unassignQuestion(tasks) : null)
}

/** How an assignee is held in the box: the pair `tasks_assign` takes, as one string. */
export function assigneeValue(kind: string, id: string): string {
  return `${kind}:${id}`
}

function groupOf(
  label: string,
  rows: readonly { readonly kind: string; readonly id: string; readonly name: string }[],
): SelectChoice[] {
  if (rows.length === 0) return []
  return [
    {
      label,
      options: rows.map((one) => ({ value: assigneeValue(one.kind, one.id), label: one.name })),
    },
  ]
}

/* ------------------------------------------------------------------- money */

type PartProps = {
  readonly field: FieldSpec
  readonly control: Control<FormValues>
  readonly name: string
  readonly label: string
}

/**
 * An amount and its currency, side by side, holding `{ major, currency }`.
 *
 * **Major units in the box, minor units on the wire.** The reader types `520` and
 * the API is sent `52000`; `ActionForm` `bodyOf` does the conversion through
 * `money.fromMajorUnits`, which is the only place in this repository an amount is
 * multiplied by a hundred (`CLAUDE.md` §Gotchas — `8.29 * 100` is not `829`).
 */
function MoneyField({ field, control, name, label }: PartProps) {
  return (
    <Stack gap="tight">
      <Row>
        <Input
          control={control}
          name={`${name}.major`}
          label={label}
          hint={field.hint}
          type="number"
          placeholder="0.00"
        />
        <Select
          control={control}
          name={`${name}.currency`}
          label="Currency"
          options={CURRENCY_OPTIONS}
        />
      </Row>
    </Stack>
  )
}

/** Both, in `packages/shared`'s own order. Written here rather than derived twice. */
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
]

/* ------------------------------------------------------------- multi-select */

/**
 * Several of a closed list, as a column of checkboxes.
 *
 * Not a `<select multiple>`: CONTRACT.md has no style for one, every operating
 * system draws it differently, and choosing two things in one on a phone is a
 * gesture most people do not know. A short list of `.check` rows is the contract's
 * own answer and is what `client/settings.html` already does for a list of switches.
 */
function MultiSelectField({ field, control, name, label }: PartProps) {
  const { field: bound, fieldState } = useController({ control, name })
  const chosen = Array.isArray(bound.value) ? (bound.value as string[]) : []
  const error = fieldState.error?.message

  return (
    <Stack gap="tight">
      <Label htmlFor={`${name}-group`}>{label}</Label>
      <div id={`${name}-group`} role="group" aria-label={label}>
        {(field.options ?? []).map((option) => (
          <label className="check" key={option.value}>
            <input
              type="checkbox"
              checked={chosen.includes(option.value)}
              onChange={(event) => {
                bound.onChange(
                  event.target.checked
                    ? [...chosen, option.value]
                    : chosen.filter((one) => one !== option.value),
                )
              }}
              onBlur={bound.onBlur}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {error === undefined && field.hint === undefined ? null : (
        <Hint alert={error !== undefined}>{error ?? field.hint}</Hint>
      )}
    </Stack>
  )
}

/* -------------------------------------------------------------------- rows */

/**
 * A repeatable group — an invoice's lines, a quote's milestones, a credit's
 * allocations.
 *
 * One level deep and no more (`schemaFields.ts` `rowFieldsOf`): a row may hold a
 * list of words and it may not hold another table, because a table of tables is a
 * component somebody has thought about, not a field a shape can produce.
 */

/**
 * `FormValues` is `Record<string, unknown>`, and `useFieldArray`'s `name` has to be
 * a path whose value is an array — over an index signature of `unknown`, that set is
 * empty and `name` narrows to `never`. `RowValues` is the same object seen as "every
 * key holds rows", which is true of exactly the fields this component is given.
 */
type RowValues = Record<string, Record<string, unknown>[]>

function RowsField({ field, control, name, label }: PartProps) {
  const array = useFieldArray<RowValues>({
    control: control as unknown as Control<RowValues>,
    name,
  })

  return (
    <Stack gap="tight">
      <Label htmlFor={`${name}-rows`}>{label}</Label>
      {field.hint === undefined ? null : <Hint>{field.hint}</Hint>}
      <div id={`${name}-rows`}>
        <Stack>
          {array.fields.map((row, index) => (
            <Stack gap="tight" key={row.id}>
              <Small>
                {label} {index + 1}
              </Small>
              {(field.fields ?? []).map((inner) => (
                <ActionField
                  key={inner.name}
                  field={inner}
                  control={control}
                  prefix={`${name}.${String(index)}.`}
                />
              ))}
              <ButtonRow>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    array.remove(index)
                  }}
                >
                  Remove
                </Button>
              </ButtonRow>
            </Stack>
          ))}
        </Stack>
      </div>
      <ButtonRow>
        <Button
          size="sm"
          onClick={() => {
            array.append(emptyRow(field))
          }}
        >
          {/*
            `Add ${label.toLowerCase()}` was the rule, and the label it read is the
            one the field is *drawn* with — the optional suffix included. On the
            Price-it form that produced **"Add add ons (optional)"**: `labelOf`
            spelling `add_ons`, and the suffix that belongs over the group and not on
            the button under it. There is no rule that turns a plural field name into
            a verb and a noun, so each is written (`actionCopy.ts` `add`) and the
            fallback drops the suffix at least.
          */}
          {field.addLabel ?? `Add ${field.label.toLowerCase()}`}
        </Button>
      </ButtonRow>
    </Stack>
  )
}
